/**
 * Linda elements module.
 *
 * @unstable
 */

import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ScrollingModule as ExperimentalScrollingModule } from '@angular/cdk-experimental/scrolling';

import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
// import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { DropzoneModule } from 'ngx-dropzone-wrapper';

import { BasicModule } from '@shared/modules/basic.module';

import { Menu } from '@shared/components/menu/menu.component';
import { Panel } from '@shared/components/panel/panel.component';
import { Avatar } from '@shared/components/avatar/avatar.component';
import { Banner } from '@shared/components/banner/banner.component';
import { Dialog } from '@shared/components/dialog/dialog.component';
import { Emblem } from '@shared/components/emblem/emblem.component';
import { Infobox } from '@shared/components/infobox/infobox.component';
import { Snackbar } from '@shared/components/snackbar/snackbar.component';
import { Popup } from '@shared/components/popup/popup.component';
import { MessageMark } from '@shared/components/message-mark/message-mark.component';
import { StatusIcon } from '@shared/components/status-icon/status-icon.component';
import { SuccessBox } from '@shared/components/success-box/success-box.component';
import { SearchExpand } from '@shared/components/search-expand/search-expand.component';

import { PanelTrigger } from '@shared/components/panel/panel.directive';

import { SinceWhen } from '@shared/pipes/since-when.pipe';

import { AccordionModule } from '@shared/components/accordion/accordion.component';
import { RouterModule } from '@angular/router';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { RandomBackground } from '@shared/components/random-bg/random-bg.component';
import { MessageStatus } from '@home/shared/pipes/message-status.pipe';
import { EditContact } from '@home/+contacts/edit-contact/edit-contact.component';
import { Searchbox } from '@shared/components/searchbox/searchbox.component';

import { SelectModule } from '@shared/components/select/select.module';
import { ToolbarModule } from '@shared/components/toolbar/toolbar.module';
import { InlineDialogModule } from '@shared/components/inline-dialog/inline-dialog.component';
import { HintDialogModule } from '@shared/components/hint-dialog/hint-dialog.component';
import { KeyToNamePipe } from '@shared/pipes/key-to-name.pipe';
import { KeyToSourcePipe } from '@shared/pipes/key-to-source.pipe';
import { SidenavModule } from '@shared/modules/sidenav/sidenav.module';
import { SourceLogo } from '@shared/components/source-logo/source-logo.component';
import { StatusFlagModule } from '@shared/modules/status-flag/status-flag.module';
import { CodeBlock } from '@shared/components/code-block/code-block.component';
import { ImageUploadModule } from '@shared/modules/image-upload/image-upload.module';
import { SpinnerModule } from '@shared/components/spinner/spinner.module';
import { InlineEdit } from '@shared/components/inline-edit/inline-edit.component';
import { StepSliderModule } from '@shared/components/step-slider/step-slider.component';
import { InputNumber } from '@shared/components/input-number/input-number.component';
import { ChipModule } from '@shared/components/chip/chip.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ScheduleDatePipe } from '@shared/pipes/schedule-date.pipe';
import { CopyToClipboardModule } from '@shared/modules/copy-to-clipboard/copy-to-clipboard.module';
import { InputSearch } from '@shared/components/input-search/input-search.component';
import { InputTooltip } from '@shared/directives/input-tooltip.directive';
import { InputTime } from '@shared/components/input-time/input-time.component';
import { PromoDialogModule } from '@shared/components/promo-dialog/promo-dialog.module';
import { UserRole } from '@shared/components/user-role/user-role.component';
import { TeamTile } from '@shared/components/team-tile/team-tile.component';
import { BetaButton } from '@shared/components/beta-button/beta-button.component';
import { BrandModule } from './brand.module';
import { SafeHtml } from '@shared/pipes/safe-html.pipe';
import { CodeInputModule } from '@shared/components/code-input/code-input.module';

@NgModule({
  bootstrap: [],
  declarations: [
    Menu,
    Panel,
    Avatar,
    Banner,
    Dialog,
    Emblem,
    Infobox,
    Snackbar,
    SourceLogo,
    StatusIcon,
    SuccessBox,
    Popup,
    CodeBlock,
    Searchbox,
    SearchExpand,
    MessageMark,
    RandomBackground,
    InputTime,
    InputNumber,
    InputSearch,
    InlineEdit,
    PanelTrigger,
    SinceWhen,
    ScheduleDatePipe,
    KeyToNamePipe,
    KeyToSourcePipe,
    MessageStatus,
    InputTooltip,
    TeamTile,
    EditContact,
    // ZefTooltipComponent,
    // ZefTooltipDirective,
    BetaButton,
  ],
  exports: [
    ChipModule,
    Menu,
    Panel,
    Avatar,
    Banner,
    Dialog,
    Emblem,
    Infobox,
    Snackbar,
    SourceLogo,
    StatusIcon,
    SuccessBox,
    Popup,
    CodeBlock,
    Searchbox,
    SearchExpand,
    MessageMark,
    RandomBackground,
    InputTime,
    InputNumber,
    InputSearch,
    InlineEdit,
    PanelTrigger,
    SinceWhen,
    ScheduleDatePipe,
    KeyToNamePipe,
    KeyToSourcePipe,
    MessageStatus,
    InputTooltip,
    TeamTile,
    AccordionModule,
    EditContact,
    UserRole,
    // ZefTooltipDirective,
    BetaButton,
    // modules
    SelectModule,
    ToolbarModule,
    SidenavModule,
    SpinnerModule,
    StatusFlagModule,
    StepSliderModule,
    ImageUploadModule,
    InlineDialogModule,
    HintDialogModule,
    PromoDialogModule,
    CopyToClipboardModule,
    ScrollingModule,
    ExperimentalScrollingModule,
    BrandModule,
    CodeInputModule,
  ],
  imports: [
    BasicModule,
    RouterModule,
    FormsModule,
    CommonModule,
    PortalModule,
    OverlayModule,
    ScrollingModule,
    ImageUploadModule,
    ExperimentalScrollingModule,
    MatIconModule,
    MatMenuModule,
    MatInputModule,
    MatDialogModule,
    MatButtonModule,
    MatOptionModule,
    // MatSelectModule,
    MatDividerModule,
    MatToolbarModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatProgressBarModule,
    MatButtonToggleModule,
    MatProgressSpinnerModule,
    DropzoneModule,
    SelectModule,
    SidenavModule,
    SpinnerModule,
    InlineDialogModule,
    HintDialogModule,
    PromoDialogModule,
    CopyToClipboardModule,
    SafeHtml,
    UserRole,
    CodeInputModule,
  ],
})
export class LindaModule {}
